import { useRef, useState, useCallback, useEffect } from 'react';

const RECORDING_SAMPLE_RATE = 16000;
const PLAYBACK_SAMPLE_RATE = 24000;

const WORKLET_CODE = `
class AudioProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input[0] && input[0].length) {
      const samples = input[0];
      const pcm16 = new Int16Array(samples.length);
      for (let i = 0; i < samples.length; i += 1) {
        pcm16[i] = Math.max(-32768, Math.min(32767, samples[i] * 32768));
      }
      this.port.postMessage(pcm16.buffer);
    }
    return true;
  }
}
registerProcessor('audio-processor', AudioProcessor);
`;

function int16ToFloat32(int16: Int16Array): Float32Array<ArrayBuffer> {
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) float32[i] = int16[i] / 32768;
  return float32;
}

export interface UseAudioPipelineReturn {
  isRecording: boolean;
  isMuted: boolean;
  mediaStream: MediaStream | null;
  ensureAudioContext: () => Promise<void>;
  startRecording: (onAudioData: (data: ArrayBuffer) => void) => Promise<void>;
  stopRecording: () => void;
  toggleMute: () => void;
  playAudio: (data: ArrayBuffer | Blob) => Promise<void>;
  stopAllPlayback: () => void;
  cleanup: () => void;
}

export const useAudioPipeline = (): UseAudioPipelineReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  // Separate contexts: playback at system rate, recording at 16kHz
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const recordingCtxRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const playbackTimeRef = useRef(0);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const keepAliveRef = useRef<OscillatorNode | null>(null);
  const mutedRef = useRef(false);
  const workletRegisteredRef = useRef(false);

  const ensureAudioContext = useCallback(async () => {
    if (!playbackCtxRef.current) {
      // Use system default sample rate — avoids macOS audio device reconfiguration.
      // Buffers created at 24kHz are resampled automatically by Web Audio API.
      playbackCtxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

      // Silent keep-alive oscillator to keep AudioContext running
      const ctx = playbackCtxRef.current;
      const keepAliveGain = ctx.createGain();
      keepAliveGain.gain.value = 0.0001;
      keepAliveGain.connect(ctx.destination);
      keepAliveRef.current = ctx.createOscillator();
      keepAliveRef.current.connect(keepAliveGain);
      keepAliveRef.current.start(0);

      playbackTimeRef.current = ctx.currentTime;
    }

    if (playbackCtxRef.current.state === 'suspended') {
      await playbackCtxRef.current.resume();
    }

    if (playbackTimeRef.current < playbackCtxRef.current.currentTime) {
      playbackTimeRef.current = playbackCtxRef.current.currentTime;
    }
  }, []);

  const stopAllPlaybackFn = useCallback(() => {
    try {
      if (keepAliveRef.current) {
        try { keepAliveRef.current.stop(0); } catch { /* already stopped */ }
        keepAliveRef.current = null;
      }
      activeSourcesRef.current.forEach((s) => {
        try { s.stop(0); } catch { /* already stopped */ }
      });
    } finally {
      activeSourcesRef.current = [];
      if (playbackCtxRef.current) {
        playbackTimeRef.current = playbackCtxRef.current.currentTime;
      }
    }
  }, []);

  const scheduleChunk = useCallback((float32: Float32Array<ArrayBuffer>) => {
    const ctx = playbackCtxRef.current;
    if (!ctx) return;

    const buffer = ctx.createBuffer(1, float32.length, PLAYBACK_SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    if (playbackTimeRef.current < now) {
      playbackTimeRef.current = now;
    }

    source.start(playbackTimeRef.current);
    playbackTimeRef.current += buffer.duration;

    activeSourcesRef.current.push(source);
    source.onended = () => {
      const index = activeSourcesRef.current.indexOf(source);
      if (index >= 0) activeSourcesRef.current.splice(index, 1);
    };
  }, []);

  const playAudio = useCallback(async (data: ArrayBuffer | Blob) => {
    try {
      let arrayBuffer: ArrayBuffer;
      if (data instanceof Blob) arrayBuffer = await data.arrayBuffer();
      else arrayBuffer = data;

      const int16 = new Int16Array(arrayBuffer);
      const float32 = int16ToFloat32(int16);

      const ctx = playbackCtxRef.current;
      if (!ctx) return;

      // Stop keep-alive on first real audio chunk
      if (keepAliveRef.current) {
        try { keepAliveRef.current.stop(0); } catch { /* already stopped */ }
        keepAliveRef.current = null;
      }

      scheduleChunk(float32);
    } catch {
      // Audio playback error - silently handle
    }
  }, [scheduleChunk]);

  const startRecording = useCallback(
    async (onAudioData: (data: ArrayBuffer) => void) => {
      if (isRecording) return;

      try {
        await ensureAudioContext();

        // Create a separate recording context at 16kHz (backend expects 16kHz PCM)
        if (!recordingCtxRef.current) {
          recordingCtxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)({
            sampleRate: RECORDING_SAMPLE_RATE,
          });
        }
        if (recordingCtxRef.current.state === 'suspended') {
          await recordingCtxRef.current.resume();
        }

        const ctx = recordingCtxRef.current;

        mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: RECORDING_SAMPLE_RATE,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });

        const source = ctx.createMediaStreamSource(mediaStreamRef.current);

        if (!workletRegisteredRef.current) {
          const workletUrl = URL.createObjectURL(
            new Blob([WORKLET_CODE], { type: 'application/javascript' }),
          );
          await ctx.audioWorklet.addModule(workletUrl);
          URL.revokeObjectURL(workletUrl);
          workletRegisteredRef.current = true;
        }

        workletNodeRef.current = new AudioWorkletNode(ctx, 'audio-processor');
        workletNodeRef.current.port.onmessage = (event: MessageEvent) => {
          if (mutedRef.current) return;
          onAudioData(event.data as ArrayBuffer);
        };

        source.connect(workletNodeRef.current);
        setMediaStream(mediaStreamRef.current);
        setIsRecording(true);
      } catch (error) {
        stopAllPlaybackFn();
        if (recordingCtxRef.current) {
          recordingCtxRef.current.close();
          recordingCtxRef.current = null;
        }
        workletRegisteredRef.current = false;
        playbackTimeRef.current = 0;
        throw error;
      }
    },
    [isRecording, ensureAudioContext, stopAllPlaybackFn],
  );

  const stopRecording = useCallback(() => {
    setIsRecording(false);
    setMediaStream(null);
    stopAllPlaybackFn();

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (recordingCtxRef.current) {
      recordingCtxRef.current.close();
      recordingCtxRef.current = null;
    }
    if (playbackCtxRef.current) {
      playbackCtxRef.current.close();
      playbackCtxRef.current = null;
    }
    workletRegisteredRef.current = false;
    playbackTimeRef.current = 0;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
  }, [stopAllPlaybackFn]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      mutedRef.current = !prev;
      return !prev;
    });
  }, []);

  const cleanup = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // Cleanup on unmount
  useEffect(() => () => {
      if (workletNodeRef.current) workletNodeRef.current.disconnect();
      if (recordingCtxRef.current) recordingCtxRef.current.close();
      if (playbackCtxRef.current) playbackCtxRef.current.close();
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      activeSourcesRef.current.forEach((s) => { try { s.stop(0); } catch { /* */ } });
    }, []);

  return {
    isRecording,
    isMuted,
    mediaStream,
    ensureAudioContext,
    startRecording,
    stopRecording,
    toggleMute,
    playAudio,
    stopAllPlayback: stopAllPlaybackFn,
    cleanup,
  };
};
